// ============================================
// Firestore Data Operations Service
// CRUD for compliance items, data requests, consent,
// retention, breach events, audit log, third-party shares
// ============================================

import {
  collection, doc, getDocs, setDoc,
  writeBatch, orderBy, query, where, limit as firestoreLimit,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  C_DATAOPS_AUDIT_LOG,
  C_DATAOPS_BREACH_EVENTS,
  C_DATAOPS_COMPLIANCE,
  C_DATAOPS_CONSENT,
  C_DATAOPS_MINOR_DETECTIONS,
  C_DATAOPS_REQUESTS,
  C_DATAOPS_RETENTION_JOBS,
  C_DATAOPS_RETENTION_POLICIES,
  C_DATAOPS_THIRD_PARTY_SHARES,
} from '../../constants/collections';
import type {
  ComplianceItem, DataRequest, ConsentRecord,
  RetentionPolicy, RetentionJob, MinorDetection,
  BreachEvent, AuditLogEntry, ThirdPartyShare,
} from '../../types/dataops';

// --- Collection refs ---

const complianceCol = () => collection(db, C_DATAOPS_COMPLIANCE);
const requestsCol = () => collection(db, C_DATAOPS_REQUESTS);
const consentCol = () => collection(db, C_DATAOPS_CONSENT);
const retentionPoliciesCol = () => collection(db, C_DATAOPS_RETENTION_POLICIES);
const retentionJobsCol = () => collection(db, C_DATAOPS_RETENTION_JOBS);
const minorDetectionsCol = () => collection(db, C_DATAOPS_MINOR_DETECTIONS);
const breachEventsCol = () => collection(db, C_DATAOPS_BREACH_EVENTS);
const auditLogCol = () => collection(db, C_DATAOPS_AUDIT_LOG);
const thirdPartySharesCol = () => collection(db, C_DATAOPS_THIRD_PARTY_SHARES);

// --- Compliance Items ---

export async function getAllComplianceItems(): Promise<ComplianceItem[]> {
  const snap = await getDocs(query(complianceCol(), orderBy('sortOrder', 'asc')));
  return snap.docs.map((d) => d.data() as ComplianceItem);
}

export async function saveComplianceItem(item: ComplianceItem): Promise<void> {
  await setDoc(doc(complianceCol(), item.id), stripUndefined(item));
}

export async function seedComplianceItems(
  items: ComplianceItem[],
  onProgress?: (msg: string) => void,
): Promise<number> {
  const batch = writeBatch(db);
  for (const item of items) {
    batch.set(doc(complianceCol(), item.id), stripUndefined(item));
  }
  await batch.commit();
  onProgress?.(`Wrote ${items.length} compliance items`);
  return items.length;
}

// --- Data Requests ---

export async function getAllDataRequests(): Promise<DataRequest[]> {
  const snap = await getDocs(query(requestsCol(), orderBy('receivedAt', 'desc')));
  return snap.docs.map((d) => d.data() as DataRequest);
}

export async function saveDataRequest(req: DataRequest): Promise<void> {
  await setDoc(doc(requestsCol(), req.id), stripUndefined(req));
}

// --- Consent Records ---

export async function getAllConsentRecords(): Promise<ConsentRecord[]> {
  const snap = await getDocs(query(consentCol(), orderBy('timestamp', 'desc')));
  return snap.docs.map((d) => d.data() as ConsentRecord);
}

export async function saveConsentRecord(record: ConsentRecord): Promise<void> {
  await setDoc(doc(consentCol(), record.id), stripUndefined(record));
}

export async function getConsentBySession(sessionId: string): Promise<ConsentRecord[]> {
  const snap = await getDocs(
    query(consentCol(), where('userId', '==', sessionId), orderBy('timestamp', 'desc')),
  );
  return snap.docs.map((d) => d.data() as ConsentRecord);
}

// --- Retention Policies ---

export async function getAllRetentionPolicies(): Promise<RetentionPolicy[]> {
  const snap = await getDocs(retentionPoliciesCol());
  return snap.docs.map((d) => d.data() as RetentionPolicy);
}

export async function saveRetentionPolicy(policy: RetentionPolicy): Promise<void> {
  await setDoc(doc(retentionPoliciesCol(), policy.id), stripUndefined(policy));
}

// --- Retention Jobs ---

export async function getAllRetentionJobs(): Promise<RetentionJob[]> {
  const snap = await getDocs(query(retentionJobsCol(), orderBy('runDate', 'desc')));
  return snap.docs.map((d) => d.data() as RetentionJob);
}

// --- Minor Detections ---

export async function getAllMinorDetections(): Promise<MinorDetection[]> {
  const snap = await getDocs(query(minorDetectionsCol(), orderBy('detectedAt', 'desc')));
  return snap.docs.map((d) => d.data() as MinorDetection);
}

export async function saveMinorDetection(detection: MinorDetection): Promise<void> {
  await setDoc(doc(minorDetectionsCol(), detection.id), stripUndefined(detection));
}

// --- Breach Events ---

export async function getAllBreachEvents(): Promise<BreachEvent[]> {
  const snap = await getDocs(query(breachEventsCol(), orderBy('detectedAt', 'desc')));
  return snap.docs.map((d) => d.data() as BreachEvent);
}

export async function saveBreachEvent(event: BreachEvent): Promise<void> {
  await setDoc(doc(breachEventsCol(), event.id), stripUndefined(event));
}

// --- Audit Log ---

export async function getAuditLog(max: number = 100): Promise<AuditLogEntry[]> {
  const snap = await getDocs(
    query(auditLogCol(), orderBy('timestamp', 'desc'), firestoreLimit(max)),
  );
  return snap.docs.map((d) => d.data() as AuditLogEntry);
}

export async function saveAuditEntry(entry: AuditLogEntry): Promise<void> {
  await setDoc(doc(auditLogCol(), entry.id), stripUndefined(entry));
}

// --- Third-Party Shares ---

export async function getAllThirdPartyShares(): Promise<ThirdPartyShare[]> {
  const snap = await getDocs(query(thirdPartySharesCol(), orderBy('timestamp', 'desc')));
  return snap.docs.map((d) => d.data() as ThirdPartyShare);
}

export async function saveThirdPartyShare(share: ThirdPartyShare): Promise<void> {
  await setDoc(doc(thirdPartySharesCol(), share.id), stripUndefined(share));
}

// --- Helpers ---

/** Strip undefined values from an object (Firestore rejects undefined) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object') ? stripUndefined(value) : value;
    }
  }
  return cleaned;
}
